import { useCallback, useState } from 'react';

export interface UseItemSelectionReturn {
  /** Set of currently selected item IDs */
  selectedItems: Set<string>;
  /** Toggle selection of a single item by ID */
  toggleItem: (itemId: string) => void;
  /** Toggle selection of all items in a group by their IDs */
  toggleGroup: (groupItemIds: string[]) => void;
  /** Check if all items in a group are selected */
  isGroupAllSelected: (groupItemIds: string[]) => boolean;
  /** Check if some (but not all) items in a group are selected */
  isGroupPartiallySelected: (groupItemIds: string[]) => boolean;
  /** Clear all selections */
  clearSelection: () => void;
}

/**
 * Hook for managing item selection state for bulk operations.
 * Encapsulates the selection logic used in ActionItemsTable.
 */
export const useItemSelection = (): UseItemSelectionReturn => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Toggle selection of a single item
  const toggleItem = useCallback((itemId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  // Toggle selection of all items in a group
  const toggleGroup = useCallback((groupItemIds: string[]) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      const allSelected = groupItemIds.every((id) => prev.has(id));
      if (allSelected) {
        // Deselect all items in the group
        groupItemIds.forEach((id) => next.delete(id));
      } else {
        // Select all items in the group
        groupItemIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  // Check if all items in a group are selected
  const isGroupAllSelected = useCallback(
    (groupItemIds: string[]) => {
      return groupItemIds.length > 0 && groupItemIds.every((id) => selectedItems.has(id));
    },
    [selectedItems],
  );

  // Check if some (but not all) items in a group are selected
  const isGroupPartiallySelected = useCallback(
    (groupItemIds: string[]) => {
      const allSelected = isGroupAllSelected(groupItemIds);
      return groupItemIds.some((id) => selectedItems.has(id)) && !allSelected;
    },
    [selectedItems, isGroupAllSelected],
  );

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  return {
    selectedItems,
    toggleItem,
    toggleGroup,
    isGroupAllSelected,
    isGroupPartiallySelected,
    clearSelection,
  };
};
